import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Clock, ShieldCheck, Sparkles } from "lucide-react";

import { disposeActiveStream, requestCameraStream } from "@/anticheat/camera";
import { useActiveStream } from "@/anticheat/react/useActiveStream"; // night-p5 B3
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CameraPreview } from "@/components/candidate/CameraPreview";
import {
  CAMERA_TILE_RESERVE,
  useCameraTileShown,
} from "@/components/candidate/cameraTile";
import { cn } from "@/lib/utils";
import { CodeQuestion, type CodeRunResult } from "@/components/CodeQuestion";
import { MCQ, shuffleStable } from "@/components/MCQ";
import { MCQMulti } from "@/components/MCQMulti";
import { PRACTICE_QUESTIONS } from "@/data/practice-questions";
import { cyrb53 } from "@/lib/colors";

// Mirror of the server-side option mixing in backend/models/snapshots.py.
// Practice.tsx uses static content that never round-trips through the API,
// so it builds the option list client-side — but the contract for MCQ /
// MCQMulti (flat `options: string[]`, no correct-answer signal) is the same.
function buildPracticeOptions(
  correct: string[],
  others: string[],
  shuffle: boolean,
  seedKey: string,
): string[] {
  const all = [...correct, ...others];
  return shuffle ? shuffleStable(all, cyrb53(seedKey)) : all;
}
import {
  anticheatAllOff,
  resolveAnticheat,
  type AnticheatConfig,
} from "@/lib/anticheatConfig";
import { fetchPreview, type PreviewResponse } from "@/lib/api";
import {
  runOwnTestsInBrowser,
  useOwnTests,
  type OwnTestResult,
} from "@/lib/ownTests";
import {
  detectFunctionName,
  getPyodide,
  runUserCodeAgainstTest,
} from "@/lib/pyodide";
import { queryKeys } from "@/lib/queryKeys";

// MVP 2.0 #7 — Practice-question prelude. Static FE-only practice phase that
// runs BEFORE the timed test starts. Untimed, unscored, zero anti-cheat,
// zero /api/events traffic. Reuses MCQ / MCQMulti / CodeQuestion subcomponents
// so the candidate sees the same UI they'll see in the real test.
//
// Question.tsx is INTENTIONALLY untouched — splitting these into separate
// route components is what makes "zero regression risk to the timed flow"
// enforceable rather than aspirational. See docs/archive/iterations/mvp-2-0.md #7.

type Phase =
  | { kind: "intro" }
  | { kind: "q"; index: number }
  | { kind: "outro" };

const PRACTICE_COUNT = PRACTICE_QUESTIONS.length;

export default function Practice() {
  const { token = "demo" } = useParams<{ token: string }>();
  const navigate = useNavigate();

  // Shared queryKey with Gate.tsx — by the time the candidate reaches the
  // outro card, this is already in TanStack Query's cache.
  const preview = useQuery<PreviewResponse>({
    queryKey: queryKeys.preview(token),
    queryFn: () => fetchPreview(token),
    staleTime: 60_000,
  });

  // QA-2 (pre-ship 2026-10-10): the question view's twin of Question.tsx's
  // B4 padding — the tile sat over the last option with nothing to scroll.
  const tileShown = useCameraTileShown();
  const [phase, setPhase] = useState<Phase>({ kind: "intro" });
  const [mcqSel, setMcqSel] = useState<(string | null)[]>(() =>
    PRACTICE_QUESTIONS.map(() => null),
  );
  const [mcqMultiSel, setMcqMultiSel] = useState<string[][]>(() =>
    PRACTICE_QUESTIONS.map(() => []),
  );
  const [code, setCode] = useState<string[]>(() =>
    PRACTICE_QUESTIONS.map((q) => q.starter_code ?? ""),
  );
  const [results, setResults] = useState<(CodeRunResult[] | null)[]>(() =>
    PRACTICE_QUESTIONS.map(() => null),
  );
  const [pyodideLoading, setPyodideLoading] = useState(true);
  const [pyodideError, setPyodideError] = useState(false);
  const [running, setRunning] = useState(false);
  // FEAT-29: the warm-up is where a candidate first meets "Your tests" — the
  // same card, per practice question, kept for the tab like everything else
  // here and sent nowhere.
  const ownTestsKey =
    phase.kind === "q" ? `practice:${token}:${phase.index}` : null;
  const [ownTests, setOwnTests] = useOwnTests(ownTestsKey);
  const [ownResults, setOwnResults] = useState<OwnTestResult[] | null>(null);
  useEffect(() => setOwnResults(null), [ownTestsKey]);

  // Load (or reload) the Pyodide runtime, tracking loading + error flags. A
  // failed load no longer silently sticks on "loading": the singleton self-heals
  // (lib/pyodide.ts clears its cache on rejection), so the CodeQuestion banner's
  // Retry — or the next Run — tries afresh. Mirrors Question.tsx's wiring.
  const loadPyodideRuntime = useCallback(() => {
    setPyodideError(false);
    setPyodideLoading(true);
    void getPyodide()
      .then(() => setPyodideLoading(false))
      .catch(() => {
        setPyodideLoading(false);
        setPyodideError(true);
      });
  }, []);

  // Preload Pyodide on mount. The candidate clicked "Try practice first" on
  // the Welcome step — intent is signalled, so paying the ~12 MB cost is fine
  // (per memory feedback_preload_after_intent). Gated on "any practice Q is
  // type=code" so a future MCQ-only practice doesn't pay the cost.
  useEffect(() => {
    const hasCode = PRACTICE_QUESTIONS.some((q) => q.type === "code");
    if (!hasCode) {
      setPyodideLoading(false);
      return;
    }
    loadPyodideRuntime();
  }, [loadPyodideRuntime]);

  // Release the camera only when the candidate actually leaves practice —
  // clicking "Start the assessment" from outro, browser back, or tab close all
  // unmount Practice.tsx and fire this cleanup. The outro card itself keeps
  // the preview alive: the candidate is still in the practice experience
  // reading "Ready when you are", and the camera light continuing through that
  // beat is the intent (Leo, 2026-05-21).
  //
  // night-p5 (B3, 2026-09-21 design review): the cleanup is deferred one tick
  // and skipped when the component is mounted again by then. React's
  // StrictMode (dev) runs mount → cleanup → mount on the first render, and a
  // bare cleanup disposed the stream the Welcome step's camera check had
  // opened — or bumped the epoch under the intro's own getUserMedia, which
  // then threw "released while pending" — so the dev stack showed "Camera
  // enabled" with no preview on any practice page, which is exactly what the
  // review reported. Production never double-invokes; the deferral costs it
  // one macrotask of camera light.
  const mountCount = useRef(0);
  useEffect(() => {
    mountCount.current += 1;
    const mine = mountCount.current;
    return () => {
      setTimeout(() => {
        if (mountCount.current === mine) disposeActiveStream();
      }, 0);
    };
  }, []);

  const goToRealTest = useCallback(() => {
    navigate(`/t/${token}`, { state: { from: "practice" } });
  }, [navigate, token]);

  const advance = useCallback(() => {
    setPhase((prev) => {
      if (prev.kind !== "q") return prev;
      const next = prev.index + 1;
      if (next >= PRACTICE_COUNT) return { kind: "outro" };
      return { kind: "q", index: next };
    });
  }, []);

  const onRun = useCallback(
    async (index: number) => {
      const q = PRACTICE_QUESTIONS[index];
      if (q.type !== "code") return;
      setRunning(true);
      // Ensure the runtime is ready first; a load failure surfaces the friendly
      // "sandbox didn't load — Retry" banner instead of a cryptic "runner" error
      // buried in the results list (mirrors Question.tsx).
      try {
        await getPyodide();
      } catch {
        setPyodideError(true);
        setResults((prev) => prev.map((v, i) => (i === index ? null : v)));
        setRunning(false);
        return;
      }
      setPyodideError(false);
      try {
        const fnName = detectFunctionName(q.starter_code, code[index]);
        const out: CodeRunResult[] = [];
        for (const t of q.example_tests ?? []) {
          const r = await runUserCodeAgainstTest(
            code[index],
            t.input,
            t.expected_output,
            fnName,
          );
          out.push({
            name: t.name,
            passed: r.passed,
            output: r.output,
            error: r.error,
          });
        }
        // FEAT-29: then the candidate's own cases (practice code is Python).
        const own = await runOwnTestsInBrowser("python", {
          code: code[index],
          functionName: fnName,
          contract: "function",
          tests: ownTests,
        });
        setResults((prev) => prev.map((v, i) => (i === index ? out : v)));
        setOwnResults(own);
      } catch (e) {
        setResults((prev) =>
          prev.map((v, i) =>
            i === index
              ? [
                  {
                    name: "runner",
                    passed: false,
                    error: e instanceof Error ? e.message : String(e),
                  },
                ]
              : v,
          ),
        );
      } finally {
        setRunning(false);
      }
    },
    [code, ownTests],
  );

  if (phase.kind === "intro") {
    return (
      <IntroPanel
        onStart={() => setPhase({ kind: "q", index: 0 })}
        onSkip={goToRealTest}
        testHasCode={preview.data?.has_code === true}
        cameraWanted={
          preview.data ? resolveAnticheat(preview.data.anticheat).camera : undefined
        }
      />
    );
  }

  if (phase.kind === "outro") {
    return (
      <OutroPanel
        testName={preview.data?.test_name}
        checks={resolveAnticheat(preview.data?.anticheat)}
        onStartReal={goToRealTest}
      />
    );
  }

  const index = phase.index;
  const q = PRACTICE_QUESTIONS[index];
  const isLast = index === PRACTICE_COUNT - 1;

  // night-p5 (B8, 2026-09-21 design review): Next is never gated here. The
  // intro promises "skip whenever you want", and the MCQ gate that used to
  // match an older Question.tsx left the only way past an unanswered practice
  // question as "Skip practice & start the test" — abandoning the warm-up and
  // starting the clock, the opposite of skipping one question. The real test
  // gates Next only on a question the recruiter marked Required
  // (`answer_required`); practice is unscored, so nothing is required.

  return (
    <div className="flex h-full flex-col bg-background">
      {/* aria-live: SPA phase transition has no URL change, so screen readers
          won't otherwise announce the new practice question. */}
      <div role="status" aria-live="polite" className="sr-only">
        Practice question {index + 1} of {PRACTICE_COUNT}
      </div>

      <header className="flex items-center justify-between gap-3 border-b border-border bg-card px-4 py-3 sm:px-6">
        <div className="flex items-center gap-3 text-sm">
          <span className="rounded-md bg-info/10 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-info">
            Practice
          </span>
          <span className="text-muted-foreground">
            Question{" "}
            <span className="font-medium tabular-nums text-foreground">
              {index + 1}
            </span>{" "}
            of{" "}
            <span className="font-medium tabular-nums text-foreground">
              {PRACTICE_COUNT}
            </span>
          </span>
        </div>
        <Button
          variant="link"
          onClick={goToRealTest}
          className="inline-flex h-auto shrink-0 cursor-pointer px-0 text-xs text-muted-foreground hover:text-foreground"
        >
          {/* Always visible — a phone candidate in practice needs a clear exit.
              Short label on mobile, full copy on ≥sm. */}
          <span className="sm:hidden">Skip</span>
          <span className="hidden sm:inline">
            Skip practice &amp; start the test
          </span>
        </Button>
        <Button
          onClick={advance}
          className="shrink-0 cursor-pointer"
          // B3: the full label pushed the header to 385px inside a 375px
          // viewport, so a phone candidate had the primary action clipped off
          // the right edge. Short label under sm, exactly like the Skip link
          // beside it; aria-label keeps the full phrase for screen readers.
          aria-label={isLast ? "Finish practice" : "Next practice question"}
        >
          <span className="sm:hidden">{isLast ? "Finish" : "Next"}</span>
          <span className="hidden sm:inline">
            {isLast ? "Finish practice" : "Next practice question"}
          </span>
        </Button>
      </header>

      <CameraPreview />

      <main className={cn("flex-1 overflow-y-auto", tileShown && CAMERA_TILE_RESERVE)}>
        {q.type === "code" ? (
          // key forces Monaco to remount when the candidate moves between
          // code questions. Without it, Editor's defaultValue is sticky (only
          // applied on mount) and Q4 would show Q3's starter. The real flow
          // dodges this because each /question/:n navigation is a full route
          // remount; Practice.tsx is a single mounted component, so we have
          // to opt in to the remount explicitly.
          <CodeQuestion
            key={`practice-code-${index}`}
            promptMd={q.prompt_md}
            exampleTests={q.example_tests ?? []}
            starterCode={q.starter_code ?? ""}
            onChange={(c) =>
              setCode((prev) => prev.map((v, i) => (i === index ? c : v)))
            }
            onRun={() => void onRun(index)}
            running={running}
            results={results[index]}
            pyodideLoading={pyodideLoading}
            pyodideError={pyodideError}
            onRetryPyodide={loadPyodideRuntime}
            // FEAT-18: the same toolbar as the real test, so the warm-up is
            // where a candidate meets Reset, Settings and Help.
            resetTo={q.starter_code ?? ""}
            onReset={() => {
              setResults((prev) => prev.map((v, i) => (i === index ? null : v)));
              setOwnResults(null);
            }}
            helpIntro={preview.data?.code_editor_instructions}
            // FEAT-29: the same "Your tests" card as the real test.
            ownTests={{
              tests: ownTests,
              onChange: (next) => {
                if (next.length !== ownTests.length) setOwnResults(null);
                setOwnTests(next);
              },
              results: ownResults,
              contract: "function",
            }}
          />
        ) : q.type === "mcq_multi" ? (
          <MCQMulti
            promptMd={q.prompt_md}
            options={buildPracticeOptions(
              q.correct_answers ?? [],
              q.other_answers ?? [],
              q.shuffle_answers,
              `practice:${index}`,
            )}
            selected={mcqMultiSel[index]}
            onSelect={(v) =>
              setMcqMultiSel((prev) =>
                prev.map((arr, i) => (i === index ? v : arr)),
              )
            }
          />
        ) : (
          <MCQ
            promptMd={q.prompt_md}
            options={buildPracticeOptions(
              q.correct_answer ? [q.correct_answer] : [],
              q.other_answers ?? [],
              q.shuffle_answers,
              `practice:${index}`,
            )}
            selected={mcqSel[index]}
            onSelect={(v) =>
              setMcqSel((prev) => prev.map((s, i) => (i === index ? v : s)))
            }
          />
        )}
      </main>
    </div>
  );
}

type CameraOptInState = "idle" | "requesting" | "granted" | "error";

function IntroPanel({
  onStart,
  onSkip,
  testHasCode,
  cameraWanted,
}: {
  onStart: () => void;
  onSkip: () => void;
  /** The real test has at least one coding question (`preview.has_code`). */
  testHasCode: boolean;
  /** FEAT-25: the real test uses the camera. `undefined` until /preview has
   *  answered — nothing is requested on a guess; `false` hides the row. */
  cameraWanted: boolean | undefined;
}) {
  const [cameraOptIn, setCameraOptIn] = useState<CameraOptInState>("idle");
  const autoRequestedRef = useRef(false);
  // night-p5 (B3): "granted" is a one-shot. The stream can end after it — an
  // OS camera toggle, another app taking the device, the dev double-mount
  // described at the dispose effect above — and the row kept saying "Camera
  // enabled — your preview will appear" over a preview that no longer
  // existed. The row follows the LIVE stream: once it is gone the opt-in link
  // comes back, and a click asks the browser again.
  const stream = useActiveStream();
  const rowState: CameraOptInState =
    cameraOptIn === "granted" && !stream ? "idle" : cameraOptIn;
  const tileShown = stream !== null;

  // Acquire the camera for the practice preview. Auto-fires on mount because
  // the user gesture chain ("Try practice first" → /practice navigation) is
  // the consent — the candidate should see the preview thumbnail the moment
  // they land on intro, not gated on a second click. If preflight already
  // acquired the singleton, requestCameraStream short-circuits and no second
  // prompt fires. If the user denies, practice continues without preview.
  const handleEnableCamera = useCallback(async () => {
    setCameraOptIn("requesting");
    try {
      await requestCameraStream();
      setCameraOptIn("granted");
    } catch {
      // Reason isn't surfaced — practice is a low-stakes warm-up, not a gate.
      setCameraOptIn("error");
    }
  }, []);

  // Auto-fire on mount. Ref guard dodges StrictMode dev double-mount sending
  // two concurrent getUserMedia calls before the singleton sets.
  useEffect(() => {
    if (cameraWanted !== true || autoRequestedRef.current) return;
    autoRequestedRef.current = true;
    void handleEnableCamera();
  }, [handleEnableCamera, cameraWanted]);

  return (
    <div
      className={cn(
        "flex min-h-full items-center justify-center bg-background p-6",
        tileShown && CAMERA_TILE_RESERVE,
      )}
    >
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Practice
          </p>
          <CardTitle className="text-2xl">A quick warm-up</CardTitle>
          <CardDescription>
            Four short questions to get you used to the interface before the
            real test starts.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* 2026-09-27 pre-ship B7: the warm-up always mixes both, but
              "same as the real test" is only true when the test has coding —
              an MCQ-only candidate was promised a coding question. The
              wording is unchanged; the clause is kept only where it holds. */}
          <ReassuranceLine
            icon={Sparkles}
            text={
              testHasCode
                ? "A mix of multiple-choice and coding — same as the real test."
                : "A mix of multiple-choice and coding."
            }
          />
          <ReassuranceLine
            icon={Clock}
            text="No timer. Move at your own pace, skip whenever you want."
          />
          <ReassuranceLine
            icon={ShieldCheck}
            text="Not recorded and not scored — feel free to experiment."
          />
          {cameraWanted !== false && (
            // night-p5 (B3): the row follows the live stream — `rowState` above.
            <CameraOptInRow state={rowState} onEnable={handleEnableCamera} />
          )}
        </CardContent>
        <CardFooter className="flex flex-col gap-2 sm:flex-row sm:gap-3">
          <Button
            type="button"
            className="w-full cursor-pointer sm:w-auto sm:flex-1"
            onClick={onStart}
          >
            Start practice
          </Button>
          <Button
            type="button"
            variant="link"
            className="cursor-pointer text-muted-foreground hover:text-foreground"
            onClick={onSkip}
          >
            Skip practice & start the test
          </Button>
        </CardFooter>
      </Card>
      <CameraPreview />
    </div>
  );
}

function CameraOptInRow({
  state,
  onEnable,
}: {
  state: CameraOptInState;
  onEnable: () => void;
}) {
  if (state === "granted") {
    return (
      <p className="text-xs text-success">
        Camera enabled — your preview will appear during practice.
      </p>
    );
  }
  if (state === "error") {
    return (
      <p className="text-xs text-muted-foreground">
        Camera unavailable — practice continues without preview.
      </p>
    );
  }
  return (
    <button
      type="button"
      onClick={onEnable}
      disabled={state === "requesting"}
      className="cursor-pointer text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
    >
      {state === "requesting"
        ? "Requesting camera…"
        : "Optional: enable camera preview for practice"}
    </button>
  );
}

function OutroPanel({
  testName,
  checks,
  onStartReal,
}: {
  testName: string | undefined;
  /** FEAT-25: the checks the real test runs — only those are announced. */
  checks: AnticheatConfig;
  onStartReal: () => void;
}) {
  const live = [
    "timer",
    checks.camera ? "camera" : null,
    checks.fullscreen || checks.activity ? "anti-cheat protections" : null,
  ].filter((part): part is string => part !== null);
  const liveLine =
    live.length === 1
      ? "From this point on the timer is live."
      : live.length === 2
        ? `From this point on the ${live[0]} and ${live[1]} are live.`
        : "From this point on the timer, camera, and anti-cheat protections are live.";
  // The browser-setup step is skipped when every check is off (Gate).
  const setupShown = !anticheatAllOff(checks);
  const tileShown = useCameraTileShown();
  return (
    <div
      className={cn(
        "flex min-h-full items-center justify-center bg-background p-6",
        tileShown && CAMERA_TILE_RESERVE,
      )}
    >
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle2
              className="h-4 w-4 text-success"
              aria-hidden="true"
            />
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Practice complete
            </p>
          </div>
          <CardTitle className="text-2xl">Ready when you are</CardTitle>
          <CardDescription>
            {testName
              ? `The ${testName} starts the moment you click below.`
              : "The assessment starts the moment you click below."}{" "}
            {liveLine}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 pt-2">
          {/* UX-17: the practice return lands on the steps AFTER Welcome, not
              on the sign-in form (Gate's `fromPractice ? { kind: "rules" }`) —
              Welcome is skipped, the rules (FEAT-16) and the browser setup
              are not, because this candidate has not seen them yet. Naming
              only the last two steps made the next screen a surprise. */}
          <p className="text-sm text-muted-foreground">
            You'll see the test rules, then be asked{" "}
            {setupShown ? "to turn off browser extensions, then " : ""}
            for your name and email
            {checks.camera ? ", then a one-time camera permission," : ""} before
            the first question.
          </p>
        </CardContent>
        <CardFooter>
          <Button
            type="button"
            className="w-full cursor-pointer"
            onClick={onStartReal}
          >
            Start the assessment
          </Button>
        </CardFooter>
      </Card>
      <CameraPreview />
    </div>
  );
}

function ReassuranceLine({
  icon: Icon,
  text,
}: {
  icon: typeof Clock;
  text: string;
}) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <Icon
        className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground"
        aria-hidden="true"
      />
      <span className="text-foreground/90">{text}</span>
    </div>
  );
}

